import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextType {
    mode: ThemeMode;      // user's chosen preference: light | dark | system
    theme: ResolvedTheme; // what is actually applied right now
    isDark: boolean;
    setMode: (m: ThemeMode) => void;
    toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'dhartinetra-theme';

function getSystemTheme(): ResolvedTheme {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(mode: ThemeMode): ResolvedTheme {
    if (mode === 'system') return getSystemTheme();
    return mode;
}

function getInitialMode(): ThemeMode {
    const saved = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
    if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
    return 'system'; // default
}

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [mode, setModeState] = useState<ThemeMode>(getInitialMode);
    const [theme, setTheme] = useState<ResolvedTheme>(() => resolveTheme(getInitialMode()));

    // Apply dark class + persist whenever mode changes
    useEffect(() => {
        const resolved = resolveTheme(mode);
        setTheme(resolved);
        localStorage.setItem(STORAGE_KEY, mode);
        if (resolved === 'dark') {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [mode]);

    // Follow OS changes when mode === 'system'
    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e: MediaQueryListEvent) => {
            if (mode === 'system') {
                const resolved = e.matches ? 'dark' : 'light';
                setTheme(resolved);
                if (resolved === 'dark') {
                    document.documentElement.classList.add('dark');
                } else {
                    document.documentElement.classList.remove('dark');
                }
            }
        };
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, [mode]);

    const setMode = (m: ThemeMode) => setModeState(m);

    // Simple toggle between light and dark (ignores system)
    const toggleTheme = () =>
        setModeState(prev => (prev === 'dark' ? 'light' : 'dark'));

    return (
        <ThemeContext.Provider value={{ mode, theme, isDark: theme === 'dark', setMode, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = (): ThemeContextType => {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
    return ctx;
};
